(ns ui.view.entry-criteria.associations
  "Association editing UI for the entry-criteria modal.

   Mirrors the per-row pattern from
   `ui.view.entity-creator.opportunity.view/contact-details-view` (label,
   existing-parties line, multi-select with create-person flow) but split
   into small components and without prefill-between-associations logic
   (v1 entry-criteria intentionally skips that).

   See `ui.view.entry-criteria.view` for the modal shell."
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.view.entity :as f-entity]
   [re-frame.core :as rf]
   [ui.editors.data-input :as data-input]
   [ui.editors.entity-creator.utils :as editor-utils]
   [ui.editors.labelled-input.view :as input]
   [ui.util :as uu]
   [ui.view.entry-criteria.model :as m]
   [ui.view.entry-criteria.utils :as entry-u]))

(defn association-row
  "One association: label + multi-select multi-person input. For a criterion
   of kind `:association-missing` the label is mandatory, an empty selection
   keeps the criterion unsatisfied, and any selection satisfies confirm. A row
   of kind `:association-optional` (added by a review) is not mandatory and
   never blocks confirm.

   `args` carries `:ident` (modal root), `:criterion`, and `:scopes->entities`."
  [{:keys [criterion scopes->entities] :as args}]
  (let [{:keys [scope association-key write-disabled? kind]} criterion
        mandatory? (= :association-missing kind)
        [source-entity-type _ _] association-key
        value @(rf/subscribe [m/association-edits
                              (assoc args :association-key association-key)])
        source-entity (get scopes->entities scope)
        label (editor-utils/association-key-label
               {:only-entity-type? nil}
               association-key)
        on-change (fn [_val opt-val]
                    (rf/dispatch [m/set-association
                                  (assoc args
                                         :association-key association-key
                                         :val opt-val)]))
        create! (fn [query]
                  (f-entity/open-creator!
                   :entity.type/person
                   {:named-value-attribute association-key
                    :ident (m/resolve-nested-association-creator-ident
                            args association-key)
                    :skip-success-msg? true
                    :start-value query
                    :on-ok!
                    (fn [{:keys [new-entity-id new-entity close-cb!]}]
                      (let [new-opt {:value new-entity-id
                                     :label (entity/display-string new-entity)}
                            next-val (conj (vec value) new-opt)]
                        (rf/dispatch [m/set-association
                                      (assoc args
                                             :association-key association-key
                                             :val next-val)])
                        (when (fn? close-cb!) (close-cb!))))}))]
    [:div
     {:class (uu/cm "flex relative gap-1.5 flex-col")}
     [input/label-elem {:label label :mandatory? mandatory?}]
     [data-input/view
      {:editor-type :editor.type/search
       :data-type :data.type/ref-multi
       :label label
       :mandatory? mandatory?
       :value value
       :on-change on-change
       :disabled? write-disabled?
       :read-only? write-disabled?
       :placeholder (loc/tr :creator-config/select-from-the-list)
       :create! (when-not write-disabled? create!)
       ;; prevent self-association (selecting source-entity as the partner)
       :exclude-values (when (and source-entity
                                  (= source-entity-type
                                     (:entity/type source-entity)))
                         #{(:db/id source-entity)})
       :field/entity-types [:entity.type/person]}]]))

(defn associations-section
  "Renders one row per `:association-missing` criterion, then one per optional
   association of the `:review` (see `entry-u/association-rows`). Caller wraps
   the result in the shared grey `block` — this fn produces only the rows.
   Returns nil when there are no rows.

   `args` carries `:ident`, `:criteria`, `:review`, and `:scopes->entities`."
  [{:keys [criteria review] :as args}]
  (let [rows (entry-u/association-rows {:criteria criteria
                                        :review review})]
    (when (seq rows)
      [:div
       {:class (uu/cm "flex flex-col gap-2")}
       (doall
        (for [criterion rows
              :let [k (:association-key criterion)]]
          ^{:key (str k)}
          [association-row (assoc args :criterion criterion)]))])))

(defn unsatisfied-association-criteria
  "Returns the seq of `:association-missing` criteria the user hasn't
   addressed yet (no edit recorded for the association-key). Drives the
   footer's confirm-disabled state."
  [{:keys [criteria] :as args}]
  (let [edits @(rf/subscribe [m/associations-edits args])]
    (->> criteria
         (filter #(= :association-missing (:kind %)))
         (remove (fn [c]
                   (seq (get edits (:association-key c))))))))

(defn collect-edits
  "Returns the current associations-edits map (any nil/empty values dropped
   defensively, though the event handler also clears empty values)."
  [args]
  (->> @(rf/subscribe [m/associations-edits args])
       (cu/filter-kv (fn [[_k v]] (seq v)))))
